import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    tipoProyecto: '',
    metrosAprox: '',
    barrio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - Presupuesto de Pladur | PLADUREX L\'Hospitalet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en L\'Hospitalet de Llobregat. Llámanos al +34 629 501 941 o solicita presupuesto sin compromiso.');
    }
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('telefono', formData.telefono);
      formBody.append('tipoProyecto', formData.tipoProyecto);
      formBody.append('metrosAprox', formData.metrosAprox);
      formBody.append('barrio', formData.barrio);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d60sotccm4ll99gupa90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          tipoProyecto: '',
          metrosAprox: '',
          barrio: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const neighborhoods = [
    'Centre', 'Santa Eulàlia', 'Collblanc-La Torrassa', 'Bellvitge', 
    'Pubilla Cases', 'Can Serra', 'La Florida', 'Gornal', 
    'Granvia Sud', 'Sanfeliu', 'Barcelona', 'Otro'
  ];

  return (
    <>
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary/10 to-primary-light/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-primary cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary font-semibold">Contacto</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-text-dark mb-6">
              Contacta con <strong>PLADUREX</strong>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Solicita tu presupuesto sin compromiso para instalación de pladur en L'Hospitalet de Llobregat
            </p>
          </motion.div>
        </div>
      </section>

      {/* Formulario y Datos de Contacto */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            {/* Columna Izquierda - Información */}
            <div className="lg:col-span-2 bg-primary-dark text-white p-8 lg:p-12">
              <motion.div {...fadeInUp}>
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Información de contacto
                </h2>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-phone-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">Teléfono</h4>
                      <a 
                        href="tel:+34629501941" 
                        className="text-white/90 hover:text-white transition-colors text-lg cursor-pointer"
                      >
                        +34 629 501 941
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-whatsapp-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">WhatsApp</h4>
                      <a 
                        href="https://wa.me/34629501941" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/90 hover:text-white transition-colors text-lg cursor-pointer"
                      >
                        Enviar mensaje
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">Área de servicio</h4>
                      <p className="text-white/90 text-lg">
                        L'Hospitalet de Llobregat + Barcelona
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">Horario</h4>
                      <p className="text-white/90 text-lg">
                        Consultar disponibilidad
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-white/20">
                  <p className="text-white/80 text-sm leading-relaxed">
                    Atendemos proyectos de pladur en L'Hospitalet de Llobregat y toda el área metropolitana de Barcelona. Presupuestos sin compromiso.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Columna Derecha - Formulario */}
            <div className="lg:col-span-3 bg-white p-8 lg:p-12">
              <motion.div {...fadeInUp}>
                <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-6">
                  Solicita tu presupuesto
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Rellena el formulario y nos pondremos en contacto contigo lo antes posible
                </p>

                <form onSubmit={handleSubmit} id="contacto-form" data-readdy-form>
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-semibold text-text-dark mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-semibold text-text-dark mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                        placeholder="+34 600 000 000"
                      />
                    </div>

                    <div>
                      <label htmlFor="tipoProyecto" className="block text-sm font-semibold text-text-dark mb-2">
                        Tipo de proyecto *
                      </label>
                      <select
                        id="tipoProyecto"
                        name="tipoProyecto"
                        value={formData.tipoProyecto}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors cursor-pointer"
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Tabiques">Tabiques</option>
                        <option value="Falsos Techos">Falsos Techos</option>
                        <option value="Trasdosados">Trasdosados</option>
                        <option value="Aislamiento">Aislamiento</option>
                        <option value="Reforma Completa">Reforma Completa</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="metrosAprox" className="block text-sm font-semibold text-text-dark mb-2">
                        Metros aproximados
                      </label>
                      <input
                        type="text"
                        id="metrosAprox"
                        name="metrosAprox"
                        value={formData.metrosAprox}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                        placeholder="Ej: 20 m²"
                      />
                    </div>

                    <div>
                      <label htmlFor="barrio" className="block text-sm font-semibold text-text-dark mb-2">
                        Barrio o zona *
                      </label>
                      <select
                        id="barrio"
                        name="barrio"
                        value={formData.barrio}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors cursor-pointer"
                      >
                        <option value="">Selecciona tu barrio</option>
                        {neighborhoods.map((neighborhood) => (
                          <option key={neighborhood} value={neighborhood}>
                            {neighborhood}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-semibold text-text-dark mb-2">
                        Mensaje
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                        placeholder="Cuéntanos más sobre tu proyecto..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-primary text-white font-bold text-lg rounded-lg hover:bg-primary-dark transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill mr-2"></i>
                          Enviar Solicitud
                        </>
                      )}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 text-center">
                        <i className="ri-checkbox-circle-line text-3xl text-green-500 mb-2"></i>
                        <p className="text-green-700 font-semibold">
                          ¡Mensaje enviado correctamente! Nos pondremos en contacto contigo pronto.
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 text-center">
                        <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
                        <p className="text-red-700 font-semibold">
                          Ha ocurrido un error. Por favor, inténtalo de nuevo o llámanos directamente.
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 text-center">
                      Al enviar este formulario aceptas que tus datos sean utilizados para contactarte sobre tu solicitud.
                    </p>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Zonas de Actuación */}
      <section className="py-16 bg-bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-4xl font-bold text-text-dark mb-4">
              Zonas de actuación
            </h2>
            <p className="text-lg text-gray-600">
              <Link to="/servicios" className="text-primary hover:text-primary-dark font-semibold transition-colors">Instalación de pladur</Link> en L'Hospitalet de Llobregat y alrededores
            </p>
          </motion.div>

          <motion.div 
            className="flex flex-wrap justify-center gap-3"
            {...fadeInUp}
          >
            {[
              'Centre', 'Santa Eulàlia', 'Collblanc-La Torrassa', 'Bellvitge', 
              'Pubilla Cases', 'Can Serra', 'La Florida', 'Gornal', 
              'Granvia Sud', 'Sanfeliu', 'Barcelona', 'Sants', 
              'Les Corts', 'Eixample', 'Cornellà', 'Esplugues',
              'El Prat', 'Sant Boi', 'Sant Just'
            ].map((zone, index) => (
              <span
                key={index}
                className="px-5 py-2.5 bg-white text-gray-700 font-medium rounded-full shadow-sm hover:shadow-md hover:text-primary transition-all duration-300"
              >
                {zone}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Enlaces Relacionados */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" {...fadeInUp}>
            <h2 className="text-3xl font-bold text-text-dark mb-4">
              Más información
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/servicios"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-service-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Nuestros Servicios
              </h3>
              <p className="text-sm text-gray-600">
                Descubre todos los servicios de pladur
              </p>
            </Link>
            <Link
              to="/empresa"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-building-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Sobre Nosotros
              </h3>
              <p className="text-sm text-gray-600">
                Conoce cómo trabajamos
              </p>
            </Link>
            <Link
              to="/faq"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-question-answer-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Preguntas Frecuentes
              </h3>
              <p className="text-sm text-gray-600">
                Resuelve tus dudas sobre pladur
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <CookieBanner />

      {/* JSON-LD Schema - ContactPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contacto - PLADUREX",
          "description": "Contacta con PLADUREX para tu proyecto de pladur en L'Hospitalet de Llobregat. Presupuesto sin compromiso.",
          "url": `${import.meta.env.VITE_SITE_URL || ''}/contacto`,
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "PLADUREX",
            "telephone": "+34629501941",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "L'Hospitalet de Llobregat",
              "addressRegion": "Barcelona",
              "addressCountry": "ES"
            }
          }
        })}
      </script>
    </>
  );
};

export default ContactoPage;
